import React from 'react';

const ExportSettingsCard = ({
  quality,
  onQualityChange,
  onResetToOriginal,
  imageReady,
  outputFormatOptions,
  supportedOutputFormats,
  outputFormat,
  onOutputFormatChange,
  isBatchMode,
  batchQueueLength,
  isBatchProcessing,
  selectedFileReady,
  isProcessing,
  onRender,
  onDownload,
  canDownloadResult,
  enabledOperations = [],
  includeAiMetadata,
  onToggleAiMetadata,
  aiMetadataAvailable,
  removeExif,
  onRemoveExifChange,
}) => {
  return (
    <section className="inspector-card !p-4 !mb-4">
      
      {/* Header & Reset */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white/90 m-0">Export & Format</h3>
        <button
          type="button"
          onClick={onResetToOriginal}
          disabled={!imageReady}
          className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-white/60 hover:text-white bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 rounded-md transition-all disabled:opacity-30 disabled:hover:bg-white/5 disabled:hover:text-white/60 disabled:hover:border-white/5"
          title="Reset all settings to original"
        >
          <span className="material-symbols-outlined text-[14px]">restart_alt</span>
          Reset
        </button>
      </div>

      {/* Inline Quality Slider */}
      <div className="flex items-center gap-3 bg-black/30 p-2.5 rounded-xl border border-white/5 mb-2 shadow-inner">
        <span className="text-[11px] text-white/50 font-medium uppercase tracking-wider pl-1">Quality</span>
        <div className="flex-1 flex items-center">
          <input
            type="range"
            min="1"
            max="100"
            value={quality}
            onChange={(e) => onQualityChange(Number(e.target.value))}
            className="studio-slider w-full !m-0"
            style={{ 
              '--fill-percent': `${quality}%`
            }}
          />
        </div>
        <div className="text-xs font-bold text-emerald-400 w-9 text-right pr-1">{quality}%</div>
      </div>

      {/* Remove EXIF Toggle */}
      <div className="flex items-center justify-between bg-black/30 p-2.5 rounded-xl border border-white/5 mb-4 shadow-inner">
        <div className="flex items-center gap-2 pl-1">
          <span className="material-symbols-outlined text-[14px] text-white/50">no_photography</span>
          <span className="text-[11px] text-white/70 font-medium uppercase tracking-wider">Remove EXIF Data</span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer mr-1">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={removeExif}
            onChange={(e) => onRemoveExifChange(e.target.checked)}
          />
          <div className="w-8 h-4 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-emerald-500"></div>
        </label>
      </div>

      {/* Compact Format Grid */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {outputFormatOptions.map((format) => {
          const isSupported = supportedOutputFormats.includes(format.value);
          const isActive = outputFormat === format.value;

          return (
            <button
              key={format.value}
              type="button"
              className={`relative py-1.5 px-1 rounded-lg text-xs font-medium transition-all duration-200 border ${
                isActive 
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.1)]' 
                  : isSupported 
                    ? 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10 text-white/70 hover:text-white' 
                    : 'bg-black/20 border-transparent text-white/20 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!isSupported) return;
                onOutputFormatChange(format.value);
              }}
              disabled={!isSupported}
              title={isSupported ? `${format.label} Format` : `${format.label} not supported by encoder`}
            >
              {format.label}
              {!isSupported && (
                <div className="absolute top-0 right-0 -mt-0.5 -mr-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500/50"></div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Render Action Button */}
      <button
        className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
          (isBatchMode ? batchQueueLength === 0 || isBatchProcessing : !selectedFileReady || isProcessing)
            ? 'bg-white/5 text-white/30 border border-white/5 cursor-not-allowed'
            : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black shadow-[0_4px_20px_rgba(16,185,129,0.2)] hover:shadow-[0_4px_25px_rgba(16,185,129,0.3)] hover:-translate-y-0.5'
        }`}
        type="button"
        onClick={onRender}
        disabled={isBatchMode ? batchQueueLength === 0 || isBatchProcessing : !selectedFileReady || isProcessing}
      >
        {(isBatchProcessing || isProcessing) && (
          <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
        )}
        {isBatchMode
          ? (isBatchProcessing ? 'Processing Batch...' : 'Run Batch')
          : (isProcessing ? 'Rendering...' : 'Export / Render')}
      </button>
    </section>
  );
};

export default ExportSettingsCard;
